import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // iOS/Safari require muted set on the element before attempting autoplay
    v.muted = true;
    v.defaultMuted = true;
    v.volume = 0;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");

    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    v.addEventListener("loadeddata", tryPlay);
    v.addEventListener("canplay", tryPlay);
    // Some mobile browsers only allow play after first interaction
    const onInteract = () => tryPlay();
    document.addEventListener("touchstart", onInteract, { once: true, passive: true });

    return () => {
      v.removeEventListener("loadeddata", tryPlay);
      v.removeEventListener("canplay", tryPlay);
      document.removeEventListener("touchstart", onInteract);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await subscribeNewsletter(email);
      toast.success("You're on the list. Welcome to the ritual.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    }
  };

  return (
    <footer data-testid="footer" className="bg-forest text-cream">
      <div data-testid="footer-video-band" className="relative h-[62vh] min-h-[420px] overflow-hidden">
        <video
          ref={videoRef}
          data-testid="footer-video"
          className="absolute inset-0 w-full h-full object-cover bg-forest"
          src={`${process.env.PUBLIC_URL}/footer-video.mp4`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={`${process.env.PUBLIC_URL}/hero-model.png`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/45 to-forest/25" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
          <p className="text-xs md:text-sm tracking-[0.28em] uppercase font-bold text-cream/80 mb-4">
            The ritual, in motion
          </p>
          <h2 className="font-sans font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02] max-w-3xl text-cream">
            Engineered to <span className="font-serif italic font-medium text-[#E8A87C]">actually taste good.</span>
          </h2>
        </div>
      </div>

      <div id="contact" className="max-w-7xl mx-auto px-6 md:px-12 pt-16 sm:pt-24 pb-10">
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-start">
          <div>
            <p className="font-serif italic text-cream/70 text-lg mb-4">Join the ritual</p>
            <h3 className="font-sans font-extrabold text-3xl md:text-4xl tracking-tight leading-tight max-w-md">
              Wellness worth chewing — straight to your inbox.
            </h3>
          </div>
          <form onSubmit={submit} className="w-full md:pt-4" data-testid="newsletter-form">
            <div className="flex items-center gap-2 border-b border-cream/30 pb-3">
              <input
                data-testid="newsletter-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-transparent text-cream placeholder:text-cream/40 text-lg outline-none"
              />
              <button data-testid="newsletter-submit" type="submit" className="p-2 rounded-full hover:bg-cream/10 transition-colors" aria-label="Subscribe">
                <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>

        <div className="mt-20 border-t border-cream/15 pt-10 grid md:grid-cols-3 gap-8 text-sm text-cream/70">
          <div>
            <p className="font-sans font-black tracking-[0.14em] text-cream text-lg">PRESTIGE</p>
            <p className="font-serif italic tracking-[0.28em] text-cream/70 text-sm">wellness co.</p>
            <p className="mt-4 max-w-xs">Three daily rituals — energy, sleep and glow — engineered to actually taste good.</p>
          </div>
          <div className="space-y-2">
            <p className="uppercase tracking-[0.2em] text-xs text-cream/50 mb-3">Support</p>
            <a href="mailto:info@prestigewellnessco.co.uk" className="block hover:text-cream transition-colors" data-testid="support-email">
              info@prestigewellnessco.co.uk
            </a>
            <p>Free UK shipping over £30</p>
            <p>Subscribe & save 15%</p>
          </div>
          <div className="space-y-2">
            <p className="uppercase tracking-[0.2em] text-xs text-cream/50 mb-3">Range</p>
            <p>Hair, Skin & Nail</p>
            <p>Deep Sleep</p>
            <p>Collagen Peptide</p>
            <p>Value Bundles</p>
          </div>
        </div>

        <div className="mt-8 overflow-hidden text-center">
          <p className="font-serif italic font-medium tracking-tight text-cream text-[6vw] leading-[1] uppercase">
            Prestige Wellness Co.
          </p>
        </div>
        <p className="mt-6 text-xs text-cream/40">© {new Date().getFullYear()} Prestige Wellness Co. All rights reserved.</p>
      </div>
    </footer>
  );
};
